import type { Bilingual } from ".";

// Buyer-facing checkout copy. Unlike the rest of the app, the storefront's
// language is NOT the dashboard toggle — it is resolved per page/store
// (page.checkout_language ?? seller default ?? "ms") and passed to translate()
// via useCheckout's bound `t`. Covers the inline / WooCommerce / TikTok layouts,
// the product sheet, and the shared gateway/channel copy.
export const checkout: Bilingual = {
  ms: {
    // form fields
    "checkout.field.name": "Nama",
    "checkout.field.fullName": "Nama penuh",
    "checkout.field.email": "E-mel",
    "checkout.field.emailFull": "Alamat e-mel",
    "checkout.field.emailOptional": "E-mel (pilihan)",
    "checkout.field.emailFullOptional": "Alamat e-mel (pilihan)",
    "checkout.field.phone": "Nombor telefon",
    "checkout.field.phoneShort": "Telefon",
    "checkout.field.phoneOptional": "Nombor telefon (pilihan)",
    "checkout.field.phoneShortOptional": "Telefon (pilihan)",
    "checkout.field.country": "Negara",
    "checkout.field.town": "Bandar",
    "checkout.field.state": "Negeri",
    "checkout.field.selectPlaceholder": "Pilih…",
    "checkout.emailHint": "Maksud anda",
    "checkout.emailTypo.title": "Domain {domain} nampak tersalah taip.",
    "checkout.emailTypo.fix": "Ya, betulkan",
    "checkout.emailTypo.keep": "Guna yang saya taip",
    "checkout.emailAutoFix.title": "Kami betulkan {domain} kepada {fixed}.",
    "checkout.emailAutoFix.undo": "Bukan? Guna yang saya taip",
    "checkout.emailComplete.hint": "ketuk untuk lengkapkan",
    "checkout.typoConfirm.title": "Semak e-mel anda dulu",
    "checkout.typoConfirm.body":
      "Pautan akses dihantar ke alamat ini sahaja. Kalau tersalah taip, e-mel itu takkan sampai.",
    "checkout.typoConfirm.youTyped": "Anda taip",
    "checkout.typoConfirm.didYouMean": "Maksud anda",
    "checkout.typoConfirm.fix": "Betulkan & teruskan bayar",
    "checkout.typoConfirm.keep": "Tak, guna yang saya taip",
    "checkout.emailAccessHint": "Kami akan hantar pautan akses anda ke sini",
    "checkout.emailReceiptHint": "Untuk resit & kemas kini penghantaran. Boleh dibiar kosong.",
    // validation / server errors
    "checkout.err.name": "Sila masukkan nama anda.",
    "checkout.err.email": "Masukkan alamat e-mel yang sah.",
    "checkout.err.emailRequired": "Sila masukkan alamat e-mel.",
    "checkout.err.emailOrPhone": "Masukkan e-mel atau nombor telefon supaya kami boleh hubungi anda.",
    "checkout.err.addProductForCode": "Tambah produk sebelum guna kod.",
    "checkout.err.applyCodeFailed": "Gagal guna kod ini. Cuba lagi.",
    "checkout.err.startPaymentFailed": "Gagal memulakan pembayaran. Sila cuba lagi.",
    "checkout.err.phoneRequired": "Sila masukkan nombor telefon untuk penghantaran.",
    "checkout.err.addressRequired": "Sila masukkan alamat penghantaran.",
    "checkout.err.postcodeRequired": "Sila masukkan poskod.",
    "checkout.err.townRequired": "Sila masukkan bandar.",
    "checkout.err.stateRequired": "Sila masukkan negeri.",
    // physical / shipping
    "checkout.shippingAddress": "Alamat penghantaran",
    "checkout.field.address": "Alamat penuh",
    "checkout.field.addressPlaceholder": "No. rumah, jalan, taman…",
    "checkout.field.postcode": "Poskod",
    // Poskod carries no asterisk on any layout, so 'optional' would otherwise be
    // byte-identical to 'required' — the buyer needs the label itself to say it
    // (same lesson as checkout.field.phoneOptional, 0154).
    "checkout.field.postcodeOptional": "Poskod (pilihan)",
    "checkout.field.postcodeHint": "Masukkan poskod anda — bandar & negeri akan terisi automatik.",
    "checkout.field.postcodeHintOptional": "Pilihan — isi jika anda mahu, atau langkau terus.",
    "checkout.field.postcodeUnknown": "Poskod tidak dikenali — anda masih boleh teruskan.",
    "checkout.shipping": "Penghantaran",
    "checkout.shippingFree": "Percuma",
    "checkout.codFeeRow": "Caj COD",
    "checkout.pay.cod": "Bayar semasa terima (COD)",
    "checkout.pay.codDesc": "Bayar tunai kepada kurier bila barang sampai.",
    "checkout.placeOrderCod": "Buat pesanan COD",
    // coupon / voucher
    "checkout.haveCoupon": "Ada kupon?",
    "checkout.clickToEnterCode": "Klik untuk masukkan kod",
    "checkout.couponPlaceholder": "Kod kupon",
    "checkout.discountCodePlaceholder": "Kod diskaun",
    "checkout.voucherPlaceholder": "Kod baucar",
    "checkout.apply": "Guna",
    "checkout.applyCoupon": "Guna kupon",
    "checkout.coupon": "Kupon",
    "checkout.removeCouponAria": "Buang kupon",
    "checkout.codeApplied": "Kod “{code}” digunakan",
    "checkout.remove": "Buang",
    "checkout.removeCodeAria": "Buang kod diskaun",
    "checkout.discountWithCode": "Diskaun ({code})",
    // order review
    "checkout.billingDetails": "Butiran pengebilan",
    "checkout.yourOrder": "Pesanan anda",
    "checkout.product": "Produk",
    "checkout.item": "Item",
    "checkout.qtyShort": "Kuantiti",
    "checkout.itemCount": "{count} item",
    "checkout.justBought": "Baru dibeli",
    "checkout.bought.from": "{name} dari {place}",
    "checkout.bought.product": "{name} beli {product}",
    "checkout.ago.now": "baru sahaja",
    "checkout.ago.min": "{n} minit lalu",
    "checkout.ago.hour": "{n} jam lalu",
    "checkout.ago.day": "{n} hari lalu",
    "checkout.amount": "Jumlah",
    "checkout.noItems": "Belum ada item dipilih.",
    "checkout.addBump": "Tambah {name} — {price}",
    "checkout.ikl.bumpTitle": "Ambil juga kupon {label} untuk {name}",
    "checkout.ikl.bumpSub": "Percuma. Kod dihantar bersama resit anda.",
    "checkout.ikl.adCoupon": "Kupon iklan {label}",
    "checkout.ikl.autoApplied": "Digunakan automatik dari iklan",
    "checkout.ikl.couponRow": "Kupon iklan",
    "checkout.ikl.gapTitle": "Tambah {amount} lagi untuk guna kupon {label}",
    "checkout.ikl.gapSub": "Kupon iklan · belanja minimum {amount}",
    "checkout.ikl.addOne": "+1 lagi · terus jimat {label}",
    "checkout.ikl.addOnePlain": "+1 lagi",
    "checkout.ikl.missTitle": "Kupon iklan {label}",
    "checkout.ikl.miss.expired": "Tempoh kupon dari iklan ini sudah tamat.",
    "checkout.ikl.miss.wrong_store": "Kupon iklan ini untuk kedai lain.",
    "checkout.ikl.miss.gone": "Kupon iklan ini tidak lagi tersedia.",
    "checkout.ikl.miss.budget_exhausted": "Kupon hari ini dah habis. Cuba lagi esok.",
    "checkout.ikl.miss.already_used": "Emel atau telefon ini sudah guna kupon ini.",
    "checkout.ikl.miss.ticket_spent": "Kupon dari pautan ini sudah digunakan.",
    "checkout.ikl.sponsored": "Ditaja",
    "checkout.addBumpYes": "Ya, tambah {name} — {price}",
    "checkout.soldCount": "{count} sudah dibeli",
    "checkout.saveBadge": "Jimat {percent}%",
    "checkout.orderSummary": "Ringkasan pesanan",
    "checkout.buyerDetails": "Butiran anda",
    "checkout.youSaved": "Anda jimat {amount}",
    "checkout.reassure": "Pautan akses dihantar ke e-mel anda serta-merta selepas bayar.",
    // totals
    "checkout.subtotal": "Jumlah kecil",
    "checkout.total": "Jumlah",
    "checkout.totalAmount": "Jumlah keseluruhan",
    // payment
    "checkout.paymentMethod": "Kaedah pembayaran",
    "checkout.bayarcashMethod": "Kaedah BayarCash",
    "checkout.noPaymentNeeded": "Tiada bayaran diperlukan — kupon menampung keseluruhan jumlah.",
    "checkout.noPaymentNeededVoucher": "Tiada bayaran diperlukan — baucar menampung jumlah penuh.",
    "checkout.gatewayUnavailable": "Pembayaran dalam talian tidak tersedia untuk halaman ini. Sila hubungi penjual.",
    "checkout.gatewayUnavailableShort": "Pembayaran dalam talian tidak tersedia. Sila hubungi penjual.",
    // submit buttons
    "checkout.redirecting": "Mengalih…",
    "checkout.placeOrderFree": "Buat pesanan — Percuma",
    "checkout.placeOrderAmount": "Buat pesanan — {total}",
    "checkout.completeOrderFree": "Lengkapkan pesanan — Percuma",
    "checkout.completePaymentAmount": "Lengkapkan pembayaran — {total}",
    "checkout.securePayment": "Pembayaran selamat & disulitkan",
    "checkout.accepted": "Diterima",
    "checkout.trust.encrypted": "Selamat & disulitkan",
    "checkout.trust.instant": "Akses segera selepas bayar",
    "checkout.trust.noSpam": "Tiada spam, selamanya",
    // qty stepper aria
    "checkout.decreaseAria": "Kurang {label}",
    "checkout.increaseAria": "Tambah {label}",
    "checkout.removeItemAria": "Buang {label}",
    // TikTok layout
    "checkout.checkout": "Checkout",
    "checkout.tt.hotSelling": "Laris! {count} terjual",
    "checkout.tt.buyerInfo": "Maklumat pembeli",
    "checkout.tt.accessEmailed": "Akses dihantar ke e-mel anda selepas bayar.",
    "checkout.tt.cartEmpty": "Troli anda kosong.",
    "checkout.tt.addProduct": "Tambah produk",
    "checkout.tt.flashSale": "Flash Sale",
    "checkout.tt.voucher": "Baucar",
    "checkout.tt.enterCode": "Masukkan kod",
    "checkout.tt.orderSummary": "Ringkasan pesanan",
    "checkout.tt.productSubtotal": "Jumlah kecil produk",
    "checkout.tt.originalPrice": "Harga asal",
    "checkout.tt.productDiscount": "Diskaun produk",
    "checkout.tt.sellerVoucher": "Baucar penjual",
    "checkout.tt.savings": "Tahniah! Anda jimat {amount} untuk pesanan ini.",
    "checkout.tt.totalItems": "Jumlah ({count} item)",
    "checkout.tt.placeOrder": "Buat Pesanan",
    // TikTok product sheet
    "checkout.ps.spec": "Spesifikasi ({count})",
    // Variasi produk (0197)
    "checkout.var.perUnit": "{price}/unit",
    "checkout.var.save": "Jimat {amount}",
    "checkout.var.saveUpTo": "Jimat sehingga {amount} dengan combo",
    "checkout.var.best": "Paling laris",
    "checkout.var.soldOut": "Habis",
    "checkout.var.notEnough": "Stok tak cukup",
    "checkout.var.left": "Tinggal {n}",
    "checkout.var.stock": "Stok {n}",
    "checkout.var.stockUnits": "Stok: {n} unit",
    "checkout.var.choices": "{n} pilihan",
    "checkout.var.qtyUnits": "{qty} × {name} = {units} unit",
    "checkout.var.label": "Variasi",
    "checkout.var.change": "Tukar",
    "checkout.quantity": "Kuantiti",
    "checkout.ps.decreaseQty": "Kurang kuantiti",
    "checkout.ps.increaseQty": "Tambah kuantiti",
    "checkout.ps.addToCart": "Masuk Troli",
    "checkout.buyNow": "Beli Sekarang",
    "checkout.gallerySwipe": "Leret",
    // TikTok flow (sales stage)
    "checkout.flow.paymentReceived": "Bayaran diterima. Setelah disahkan, pautan akses anda akan dihantar ke e-mel. Anda boleh tutup halaman ini.",
    "checkout.flow.reviewCart": "Semak troli ({count})",
    "checkout.flow.cart": "Troli",
    "checkout.flow.from": "Mulai dari ",
    // gateway copy (checkoutShared)
    "gw.toyyibpay.label": "FPX / Perbankan Online",
    "gw.toyyibpay.subtitle": "Maybank, CIMB & 16 bank · Dilindungi toyyibPay",
    "gw.stripe.label": "Kad",
    "gw.stripe.subtitle": "Visa · Mastercard · Dilindungi Stripe",
    "gw.bayarcash.label": "BayarCash",
    "gw.bayarcash.subtitle": "Perbankan online · DuitNow QR · kad · Dilindungi BayarCash",
    "gw.chip.label": "Kad, FPX & e-wallet",
    "gw.chip.subtitle": "Kad · FPX · TNG/ShopeePay · DuitNow QR · Dilindungi CHIP",
    // Follows Atome's own merchant wording, and reads as a sentence continuing
    // from their logo — which renders immediately before it, not as a label.
    "gw.atome.label": "- 0% faedah, 3 bayaran mudah",
    "gw.billplz.label": "Billplz — FPX / Perbankan Online",
    "gw.billplz.subtitle": "Maybank, CIMB & bank utama · Dilindungi Billplz",
    "gw.chip.fpx.label": "FPX / Perbankan Online",
    "gw.chip.fpx.subtitle": "Maybank, CIMB & 16 bank · Dilindungi CHIP",
    "gw.chip.duitnow_qr.label": "DuitNow QR",
    "gw.chip.duitnow_qr.subtitle": "Imbas & bayar dengan mana-mana app bank/e-wallet · Dilindungi CHIP",
    "gw.chip.card.label": "Kad kredit/debit",
    "gw.chip.card.subtitle": "Visa · Mastercard · Dilindungi CHIP",
    "gw.chip.ewallet.label": "E-wallet",
    "gw.chip.ewallet.subtitle": "TNG · ShopeePay · GrabPay · Dilindungi CHIP",
    "bc.fpx": "FPX (perbankan online)",
    "bc.duitnow_qr": "DuitNow QR",
    "bc.card": "Kad debit/kredit",
    "bc.spaylater": "SPayLater",
    "bc.boost_payflex": "Boost PayFlex",
  },
  en: {
    // form fields
    "checkout.field.name": "Name",
    "checkout.field.fullName": "Full name",
    "checkout.field.email": "Email",
    "checkout.field.emailFull": "Email address",
    "checkout.field.emailOptional": "Email (optional)",
    "checkout.field.emailFullOptional": "Email address (optional)",
    "checkout.field.phone": "Phone number",
    "checkout.field.phoneShort": "Phone",
    "checkout.field.phoneOptional": "Mobile number (optional)",
    "checkout.field.phoneShortOptional": "Phone (optional)",
    "checkout.field.country": "Country",
    "checkout.field.town": "Town",
    "checkout.field.state": "State",
    "checkout.field.selectPlaceholder": "Select…",
    "checkout.emailHint": "Did you mean",
    "checkout.emailTypo.title": "The domain {domain} looks like a typo.",
    "checkout.emailTypo.fix": "Yes, fix it",
    "checkout.emailTypo.keep": "Use what I typed",
    "checkout.emailAutoFix.title": "We corrected {domain} to {fixed}.",
    "checkout.emailAutoFix.undo": "Not right? Use what I typed",
    "checkout.emailComplete.hint": "tap to complete",
    "checkout.typoConfirm.title": "Check your email first",
    "checkout.typoConfirm.body":
      "Your access link goes to this address and nowhere else. If it's mistyped, the email won't reach you.",
    "checkout.typoConfirm.youTyped": "You typed",
    "checkout.typoConfirm.didYouMean": "Did you mean",
    "checkout.typoConfirm.fix": "Fix it & continue to pay",
    "checkout.typoConfirm.keep": "No, use what I typed",
    "checkout.emailAccessHint": "We'll send your access link here",
    "checkout.emailReceiptHint": "For your receipt & delivery updates. You can leave this blank.",
    // validation / server errors
    "checkout.err.name": "Please enter your name.",
    "checkout.err.email": "Enter a valid email address.",
    "checkout.err.emailRequired": "Please enter your email address.",
    "checkout.err.emailOrPhone": "Enter an email or a phone number so we can reach you.",
    "checkout.err.addProductForCode": "Add a product before applying a code.",
    "checkout.err.applyCodeFailed": "Could not apply this code. Try again.",
    "checkout.err.startPaymentFailed": "Could not start payment. Please try again.",
    "checkout.err.phoneRequired": "Please enter a phone number for delivery.",
    "checkout.err.addressRequired": "Please enter a delivery address.",
    "checkout.err.postcodeRequired": "Please enter a postcode.",
    "checkout.err.townRequired": "Please enter a town/city.",
    "checkout.err.stateRequired": "Please enter a state.",
    // physical / shipping
    "checkout.shippingAddress": "Shipping address",
    "checkout.field.address": "Full address",
    "checkout.field.addressPlaceholder": "House no., street, area…",
    "checkout.field.postcode": "Postcode",
    "checkout.field.postcodeOptional": "Postcode (optional)",
    "checkout.field.postcodeHint": "Enter your postcode — city & state fill in automatically.",
    "checkout.field.postcodeHintOptional": "Optional — fill it in if you like, or skip it.",
    "checkout.field.postcodeUnknown": "Postcode not recognised — you can still continue.",
    "checkout.shipping": "Shipping",
    "checkout.shippingFree": "Free",
    "checkout.codFeeRow": "COD charge",
    "checkout.pay.cod": "Pay on delivery (COD)",
    "checkout.pay.codDesc": "Pay the courier in cash when your order arrives.",
    "checkout.placeOrderCod": "Place COD order",
    // coupon / voucher
    "checkout.haveCoupon": "Have a coupon?",
    "checkout.clickToEnterCode": "Click to enter a code",
    "checkout.couponPlaceholder": "Coupon code",
    "checkout.discountCodePlaceholder": "Discount code",
    "checkout.voucherPlaceholder": "Voucher code",
    "checkout.apply": "Apply",
    "checkout.applyCoupon": "Apply coupon",
    "checkout.coupon": "Coupon",
    "checkout.removeCouponAria": "Remove coupon",
    "checkout.codeApplied": "Code “{code}” applied",
    "checkout.remove": "Remove",
    "checkout.removeCodeAria": "Remove discount code",
    "checkout.discountWithCode": "Discount ({code})",
    // order review
    "checkout.billingDetails": "Billing details",
    "checkout.yourOrder": "Your order",
    "checkout.product": "Product",
    "checkout.item": "Item",
    "checkout.qtyShort": "Qty",
    "checkout.itemCount": "{count} item(s)",
    "checkout.justBought": "Just bought",
    "checkout.bought.from": "{name} from {place}",
    "checkout.bought.product": "{name} bought {product}",
    "checkout.ago.now": "just now",
    "checkout.ago.min": "{n} min ago",
    "checkout.ago.hour": "{n}h ago",
    "checkout.ago.day": "{n}d ago",
    "checkout.amount": "Amount",
    "checkout.noItems": "No items selected yet.",
    "checkout.addBump": "Add {name} — {price}",
    "checkout.ikl.bumpTitle": "Also take a {label} coupon for {name}",
    "checkout.ikl.bumpSub": "Free. The code comes with your receipt.",
    "checkout.ikl.adCoupon": "{label} ad coupon",
    "checkout.ikl.autoApplied": "Applied automatically from the ad",
    "checkout.ikl.couponRow": "Ad coupon",
    "checkout.ikl.gapTitle": "Add {amount} more to use the {label} coupon",
    "checkout.ikl.gapSub": "Ad coupon · minimum spend {amount}",
    "checkout.ikl.addOne": "+1 more · save {label}",
    "checkout.ikl.addOnePlain": "+1 more",
    "checkout.ikl.missTitle": "{label} ad coupon",
    "checkout.ikl.miss.expired": "This ad's coupon has expired.",
    "checkout.ikl.miss.wrong_store": "This ad coupon is for another shop.",
    "checkout.ikl.miss.gone": "This ad coupon is no longer available.",
    "checkout.ikl.miss.budget_exhausted": "Today's coupons are gone. Try again tomorrow.",
    "checkout.ikl.miss.already_used": "This email or phone has already used this coupon.",
    "checkout.ikl.miss.ticket_spent": "The coupon from this link has been used.",
    "checkout.ikl.sponsored": "Sponsored",
    "checkout.addBumpYes": "Yes, add {name} — {price}",
    "checkout.soldCount": "{count} already bought",
    "checkout.saveBadge": "Save {percent}%",
    "checkout.orderSummary": "Order summary",
    "checkout.buyerDetails": "Your details",
    "checkout.youSaved": "You saved {amount}",
    "checkout.reassure": "Your access link is emailed to you instantly after payment.",
    // totals
    "checkout.subtotal": "Subtotal",
    "checkout.total": "Total",
    "checkout.totalAmount": "Total amount",
    // payment
    "checkout.paymentMethod": "Payment method",
    "checkout.bayarcashMethod": "BayarCash method",
    "checkout.noPaymentNeeded": "No payment needed — your coupon covers the full total.",
    "checkout.noPaymentNeededVoucher": "No payment needed — the voucher covers the full total.",
    "checkout.gatewayUnavailable": "Online payment isn’t available for this page right now. Please contact the seller.",
    "checkout.gatewayUnavailableShort": "Online payment isn’t available. Please contact the seller.",
    // submit buttons
    "checkout.redirecting": "Redirecting…",
    "checkout.placeOrderFree": "Place order — Free",
    "checkout.placeOrderAmount": "Place order — {total}",
    "checkout.completeOrderFree": "Complete order — Free",
    "checkout.completePaymentAmount": "Complete payment — {total}",
    "checkout.securePayment": "Secure & encrypted payment",
    "checkout.accepted": "Accepted",
    "checkout.trust.encrypted": "Secured & encrypted",
    "checkout.trust.instant": "Instant access after payment",
    "checkout.trust.noSpam": "No spam, ever",
    // qty stepper aria
    "checkout.decreaseAria": "Decrease {label}",
    "checkout.increaseAria": "Increase {label}",
    "checkout.removeItemAria": "Remove {label}",
    // TikTok layout
    "checkout.checkout": "Checkout",
    "checkout.tt.hotSelling": "Hot! {count} sold",
    "checkout.tt.buyerInfo": "Buyer details",
    "checkout.tt.accessEmailed": "Access is emailed to you after payment.",
    "checkout.tt.cartEmpty": "Your cart is empty.",
    "checkout.tt.addProduct": "Add a product",
    "checkout.tt.flashSale": "Flash Sale",
    "checkout.tt.voucher": "Voucher",
    "checkout.tt.enterCode": "Enter code",
    "checkout.tt.orderSummary": "Order summary",
    "checkout.tt.productSubtotal": "Product subtotal",
    "checkout.tt.originalPrice": "Original price",
    "checkout.tt.productDiscount": "Product discount",
    "checkout.tt.sellerVoucher": "Seller voucher",
    "checkout.tt.savings": "Congrats! You saved {amount} on this order.",
    "checkout.tt.totalItems": "Total ({count} items)",
    "checkout.tt.placeOrder": "Place Order",
    // TikTok product sheet
    "checkout.ps.spec": "Specifications ({count})",
    // Product variations (0197)
    "checkout.var.perUnit": "{price}/unit",
    "checkout.var.save": "Save {amount}",
    "checkout.var.saveUpTo": "Save up to {amount} with a combo",
    "checkout.var.best": "Best seller",
    "checkout.var.soldOut": "Sold out",
    "checkout.var.notEnough": "Not enough stock",
    "checkout.var.left": "{n} left",
    "checkout.var.stock": "Stock {n}",
    "checkout.var.stockUnits": "Stock: {n} units",
    "checkout.var.choices": "{n} options",
    "checkout.var.qtyUnits": "{qty} × {name} = {units} units",
    "checkout.var.label": "Variation",
    "checkout.var.change": "Change",
    "checkout.quantity": "Quantity",
    "checkout.ps.decreaseQty": "Decrease quantity",
    "checkout.ps.increaseQty": "Increase quantity",
    "checkout.ps.addToCart": "Add to cart",
    "checkout.buyNow": "Buy Now",
    "checkout.gallerySwipe": "Swipe",
    // TikTok flow (sales stage)
    "checkout.flow.paymentReceived": "Payment received. Once it’s confirmed, your access link is emailed to you. You can close this page.",
    "checkout.flow.reviewCart": "Review cart ({count})",
    "checkout.flow.cart": "Cart",
    "checkout.flow.from": "From ",
    // gateway copy (checkoutShared)
    "gw.toyyibpay.label": "FPX / Online Banking",
    "gw.toyyibpay.subtitle": "Maybank, CIMB & 16 banks · Secured by toyyibPay",
    "gw.stripe.label": "Card",
    "gw.stripe.subtitle": "Visa · Mastercard · Secured by Stripe",
    "gw.bayarcash.label": "BayarCash",
    "gw.bayarcash.subtitle": "Online banking · DuitNow QR · card · Secured by BayarCash",
    "gw.chip.label": "Card, FPX & e-wallet",
    "gw.chip.subtitle": "Cards · FPX · TNG/ShopeePay · DuitNow QR · Secured by CHIP",
    "gw.atome.label": "- 0% interest over 3 easy payments",
    "gw.billplz.label": "Billplz — FPX / Online Banking",
    "gw.billplz.subtitle": "Maybank, CIMB & major banks · Secured by Billplz",
    "gw.chip.fpx.label": "FPX / Online Banking",
    "gw.chip.fpx.subtitle": "Maybank, CIMB & 16 banks · Secured by CHIP",
    "gw.chip.duitnow_qr.label": "DuitNow QR",
    "gw.chip.duitnow_qr.subtitle": "Scan & pay with any bank/e-wallet app · Secured by CHIP",
    "gw.chip.card.label": "Credit/debit card",
    "gw.chip.card.subtitle": "Visa · Mastercard · Secured by CHIP",
    "gw.chip.ewallet.label": "E-wallet",
    "gw.chip.ewallet.subtitle": "TNG · ShopeePay · GrabPay · Secured by CHIP",
    "bc.fpx": "FPX (online banking)",
    "bc.duitnow_qr": "DuitNow QR",
    "bc.card": "Debit/credit card",
    "bc.spaylater": "SPayLater",
    "bc.boost_payflex": "Boost PayFlex",
  },
};
